"use client"

import {
  MouseEvent,
  PointerEvent,
  useEffect,
  useRef,
  useState,
} from "react"
import { ArrowLeft, ArrowRight } from "@medusajs/icons"
import Image from "next/image"
import { useFrontendPreferences } from "@lib/frontend-preferences"
import { getLocalizedTaxonomyTitle } from "@lib/product-content-localization"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

export type CategoryMediaRailItem = {
  id: string
  handle: string
  name: string
  thumbnail: string | undefined
}

type CategoryMediaRailProps = {
  categories?: CategoryMediaRailItem[]
}

const CategoryMediaRail = ({ categories = [] }: CategoryMediaRailProps) => {
  const { locale, t } = useFrontendPreferences()
  const viewportRef = useRef<HTMLDivElement>(null)
  const dragStartX = useRef<number | null>(null)
  const dragStartScrollLeft = useRef(0)
  const didDrag = useRef(false)
  const [isDragging, setIsDragging] = useState(false)
  const [canMovePrevious, setCanMovePrevious] = useState(false)
  const [canMoveNext, setCanMoveNext] = useState(false)

  const updateControlAvailability = () => {
    const viewport = viewportRef.current
    if (!viewport) return

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth)
    setCanMovePrevious(viewport.scrollLeft > 3)
    setCanMoveNext(viewport.scrollLeft < maxScrollLeft - 3)
  }

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const frame = window.requestAnimationFrame(updateControlAvailability)
    const resizeObserver = new ResizeObserver(updateControlAvailability)
    viewport.addEventListener("scroll", updateControlAvailability, {
      passive: true,
    })
    resizeObserver.observe(viewport)

    return () => {
      window.cancelAnimationFrame(frame)
      viewport.removeEventListener("scroll", updateControlAvailability)
      resizeObserver.disconnect()
    }
  }, [categories.length])

  const getCardOffsets = () => {
    const viewport = viewportRef.current
    if (!viewport) return []

    const cards = Array.from(viewport.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement
    )
    const firstOffset = cards[0]?.offsetLeft ?? 0

    return cards.map((card) => card.offsetLeft - firstOffset)
  }

  const settleRail = () => {
    const viewport = viewportRef.current
    if (!viewport || !didDrag.current) return

    const offsets = getCardOffsets()
    if (!offsets.length) return

    const target = offsets.reduce((closest, offset) =>
      Math.abs(offset - viewport.scrollLeft) <
      Math.abs(closest - viewport.scrollLeft)
        ? offset
        : closest
    )

    viewport.scrollTo({ left: target, behavior: "smooth" })
  }

  const moveRail = (direction: -1 | 1) => {
    const viewport = viewportRef.current
    const offsets = getCardOffsets()
    if (!viewport || !offsets.length) return

    const current = viewport.scrollLeft
    const target =
      direction > 0
        ? offsets.find((offset) => offset > current + 3) ??
          offsets[offsets.length - 1]
        : [...offsets].reverse().find((offset) => offset < current - 3) ??
          offsets[0]

    viewport.scrollTo({ left: target, behavior: "smooth" })
  }

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return
    const viewport = viewportRef.current
    if (!viewport) return

    dragStartX.current = event.clientX
    dragStartScrollLeft.current = viewport.scrollLeft
    didDrag.current = false
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current
    if (!viewport || dragStartX.current === null) return

    const distance = event.clientX - dragStartX.current
    if (Math.abs(distance) < 5) return

    event.preventDefault()
    if (!didDrag.current) {
      didDrag.current = true
      setIsDragging(true)
      viewport.setPointerCapture(event.pointerId)
    }

    viewport.style.scrollBehavior = "auto"
    viewport.scrollLeft = dragStartScrollLeft.current - distance
  }

  const handlePointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    const viewport = viewportRef.current
    if (viewport?.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId)
    }

    dragStartX.current = null
    setIsDragging(false)
    viewport?.style.removeProperty("scroll-behavior")
    settleRail()
  }

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!didDrag.current) return
    event.preventDefault()
    event.stopPropagation()
    didDrag.current = false
  }

  if (!categories.length) return null

  return (
    <section className="sahil-category-media-rail" aria-label="Category media">
      <div className="content-container">
        <div className="sahil-reference-section-heading">
          <div>
            <p>{t.categoryIndex.eyebrow}</p>
            <h2>{t.categoryIndex.title}</h2>
          </div>
        </div>
      </div>
      <div className="sahil-category-media-rail__stage">
        <div
          ref={viewportRef}
          className={`sahil-category-media-rail__viewport ${
            isDragging ? "is-dragging" : ""
          }`}
          onClickCapture={handleClickCapture}
          onDragStart={(event) => event.preventDefault()}
          onPointerDownCapture={handlePointerDown}
          onPointerMoveCapture={handlePointerMove}
          onPointerUpCapture={handlePointerEnd}
          onPointerCancelCapture={handlePointerEnd}
        >
          {categories.map((category) => (
            <LocalizedClientLink
              key={category.id}
              href={`/categories/${category.handle}`}
              className="sahil-category-media-rail__card"
              dir={locale === "fa" ? "rtl" : "ltr"}
            >
              <span className="relative sahil-category-media-rail__image">
                {category.thumbnail ? (
                  <Image
                    src={category.thumbnail}
                    alt={getLocalizedTaxonomyTitle(category.name, locale)}
                    fill
                    sizes="(min-width: 1024px) 25vw, (min-width: 640px) 40vw, 80vw"
                    draggable={false}
                  />
                ) : (
                  <span className="sahil-category-media-rail__placeholder">
                    {getLocalizedTaxonomyTitle(category.name, locale)}
                  </span>
                )}
              </span>
              <span className="sahil-category-media-rail__label">
                <span>{getLocalizedTaxonomyTitle(category.name, locale)}</span>
                <span className="sahil-category-media-rail__go" aria-hidden="true">
                  <ArrowRight className="rtl:rotate-180" />
                </span>
              </span>
            </LocalizedClientLink>
          ))}
        </div>

        <div className="sahil-category-media-rail__controls">
          {canMovePrevious && (
            <button
              type="button"
              className="sahil-category-media-rail__control sahil-category-media-rail__control--previous"
              onClick={() => moveRail(-1)}
              aria-label="Previous category"
            >
              <ArrowLeft aria-hidden="true" />
            </button>
          )}
          {canMoveNext && (
            <button
              type="button"
              className="sahil-category-media-rail__control sahil-category-media-rail__control--next"
              onClick={() => moveRail(1)}
              aria-label="Next category"
            >
              <ArrowRight aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

export default CategoryMediaRail
